import React, { useEffect, useState } from 'react';
import Video, { Participant } from 'twilio-video';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { getCodecChoice } from '../../utils/useConnectionOptions/useConnectionOptions';

// A diagnostic overlay for tracking down media problems on a device we cannot
// attach a debugger to (for example an iPhone). It is shown only when the page
// URL has a `debug` query parameter (e.g. ...&debug=1) and is otherwise not
// rendered. It reads state only; it changes nothing about the call.

export function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  // Remember it for the session: the app may rewrite the URL (dropping the query)
  // after it reads the join token.
  try {
    if (/[?&]debug(=|&|$)/.test(window.location.search)) {
      window.sessionStorage.setItem('vuiDebug', '1');
      return true;
    }
    return window.sessionStorage.getItem('vuiDebug') === '1';
  } catch (e) {
    return /[?&]debug(=|&|$)/.test(window.location.search);
  }
}

const MAX_EVENTS = 12;

function stamp(): string {
  return new Date().toISOString().slice(11, 19);
}

function describeVideoElements(): string[] {
  return Array.from(document.querySelectorAll('video')).map((el, i) => {
    const stream = el.srcObject as MediaStream | null;
    const tracks = stream
      ? stream
          .getVideoTracks()
          .map(t => `${t.readyState}${t.muted ? '/muted' : ''}${t.enabled ? '' : '/disabled'}`)
          .join(',')
      : 'no stream';
    return `el${i}: media ${el.videoWidth}x${el.videoHeight} box ${el.clientWidth}x${el.clientHeight} paused=${
      el.paused
    } ready=${el.readyState} [${tracks}]`;
  });
}

// A diagnostic must never take the call down: if the overlay throws, render nothing.
export class DebugBoundary extends React.Component<React.PropsWithChildren<{}>, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export default function DebugOverlay() {
  const { room } = useVideoContext();
  const [events, setEvents] = useState<string[]>([]);
  const [snapshot, setSnapshot] = useState<string[]>([]);
  const [stats, setStats] = useState<string[]>([]);

  const log = (message: string) => setEvents(prev => [`${stamp()} ${message}`, ...prev].slice(0, MAX_EVENTS));

  // Subscription and switch-on/off events for every remote participant.
  useEffect(() => {
    // Before the call connects, `room` is a bare EventEmitter with no participants.
    if (!room || !room.on || !room.participants) return;
    const cleanups: Array<() => void> = [];

    const watch = (p: Participant) => {
      const id = p.identity;
      const onSubscribed = (track: any) => {
        log(`${id} subscribed ${track.kind}`);
        if (track.kind === 'video' && track.on) {
          track.on('switchedOn', () => log(`${id} video switched ON`));
          track.on('switchedOff', () => log(`${id} video switched OFF`));
        }
      };
      const onUnsubscribed = (track: any) => log(`${id} unsubscribed ${track.kind}`);
      const onFailed = (error: any, publication: any) =>
        log(`${id} SUBSCRIBE FAILED ${publication && publication.kind} code=${error && error.code} ${error && error.message}`);
      p.on('trackSubscribed', onSubscribed);
      p.on('trackUnsubscribed', onUnsubscribed);
      p.on('trackSubscriptionFailed', onFailed);
      cleanups.push(() => {
        p.off('trackSubscribed', onSubscribed);
        p.off('trackUnsubscribed', onUnsubscribed);
        p.off('trackSubscriptionFailed', onFailed);
      });
    };

    room.participants.forEach(watch);
    const onConnected = (p: Participant) => {
      log(`${p.identity} connected`);
      watch(p);
    };
    const onDisconnected = (p: Participant) => log(`${p.identity} disconnected`);
    room.on('participantConnected', onConnected);
    room.on('participantDisconnected', onDisconnected);
    cleanups.push(() => {
      room.off('participantConnected', onConnected);
      room.off('participantDisconnected', onDisconnected);
    });

    return () => cleanups.forEach(c => c());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  // Current state, refreshed every second.
  useEffect(() => {
    const read = () => {
      const lines: string[] = [];
      lines.push(`twilio-video ${(Video as any).version} supported=${Video.isSupported}`);
      lines.push(navigator.userAgent.replace(/Mozilla\/5\.0 /, ''));
      lines.push(`room ${room ? room.state : 'none'}  codec pref: ${getCodecChoice() || 'default (vp8)'}`);
      if (room && room.localParticipant) {
        const lp: any = room.localParticipant;
        lp.tracks.forEach((pub: any) => {
          const mst = pub.track && pub.track.mediaStreamTrack;
          lines.push(
            `me ${pub.kind} en=${pub.isTrackEnabled} ${mst ? `${mst.readyState}${mst.muted ? '/muted' : ''}` : 'no track'}`
          );
        });
        room.participants.forEach((p: any) => {
          p.tracks.forEach((pub: any) => {
            const t = pub.track;
            const mst = t && t.mediaStreamTrack;
            lines.push(
              `${p.identity} ${pub.kind} sub=${pub.isSubscribed} en=${pub.isTrackEnabled}` +
                (t
                  ? ` started=${t.isStarted} switchedOff=${t.isSwitchedOff} ${mst ? `${mst.readyState}${mst.muted ? '/muted' : ''}` : ''}`
                  : ' NO TRACK')
            );
          });
        });
      }
      lines.push(...describeVideoElements());
      setSnapshot(lines);
    };
    read();
    const timer = window.setInterval(read, 1000);
    return () => window.clearInterval(timer);
  }, [room]);

  // What is actually being received and sent, from the peer connection: codec, bytes and
  // packets (are RTP packets arriving at all?), decoded frame size and rate. Two samples
  // show whether the byte counts are growing.
  useEffect(() => {
    if (!room || !(room as any).getStats) return;
    const timer = window.setInterval(() => {
      (room as any)
        .getStats()
        .then((reports: any[]) => {
          const lines: string[] = [];
          reports.forEach(r => {
            (r.remoteVideoTrackStats || []).forEach((t: any) =>
              lines.push(
                `rx video ${t.codec} ${t.dimensions ? `${t.dimensions.width}x${t.dimensions.height}` : '-'} fps=${
                  t.frameRate
                } bytes=${t.bytesReceived} pkts=${t.packetsReceived} lost=${t.packetsLost}`
              )
            );
            (r.remoteAudioTrackStats || []).forEach((t: any) =>
              lines.push(`rx audio ${t.codec} bytes=${t.bytesReceived} pkts=${t.packetsReceived} lost=${t.packetsLost}`)
            );
            (r.localVideoTrackStats || []).forEach((t: any) =>
              lines.push(
                `tx video ${t.codec} ${t.dimensions ? `${t.dimensions.width}x${t.dimensions.height}` : '-'} bytes=${
                  t.bytesSent
                } pkts=${t.packetsSent}`
              )
            );
          });
          setStats(lines);
        })
        .catch(() => setStats(['getStats failed']));
    }, 2000);
    return () => window.clearInterval(timer);
  }, [room]);

  // Tap to call play() on every video element and log exactly why iOS refuses, if
  // it does: tells an autoplay block apart from "no frames ever arrive".
  const forcePlay = () => {
    Array.from(document.querySelectorAll('video')).forEach((el, i) => {
      el.muted = true;
      el.play().then(
        () => log(`el${i} play() ok`),
        (e: any) => log(`el${i} play() FAILED ${e && e.name}: ${e && e.message}`)
      );
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        pointerEvents: 'none',
        background: 'rgba(0,0,0,0.78)',
        color: '#7CFC00',
        font: '10px/1.25 monospace',
        padding: '4px 6px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
      }}
    >
      {snapshot.join('\n')}
      {'\n--- stats ---\n'}
      {stats.join('\n')}
      {'\n--- events ---\n'}
      {events.join('\n')}
      <div>
        <button
          onClick={forcePlay}
          style={{ pointerEvents: 'auto', font: '12px monospace', padding: '4px 8px', marginTop: 4 }}
        >
          force play all video
        </button>
      </div>
    </div>
  );
}
